import React from "react";
import styled, { css } from "styled-components";
import theme from "../../theme";

/* ---------------------------------- Panel --------------------------------- */

export const Panel = styled.section`
  background: ${theme.color.surface};
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.sm};
  padding: ${({ $flush }) => ($flush ? "0" : "24px")};
  overflow: hidden;
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: ${({ $inset }) => ($inset ? "20px 24px" : "0 0 20px")};
  border-bottom: ${({ $divided }) =>
    $divided ? `1px solid ${theme.color.border}` : "none"};
  flex-wrap: wrap;

  h2,
  h3 {
    font-size: ${theme.font.h3};
    font-weight: 600;
    color: ${theme.color.text};
    line-height: 1.4;
  }

  p {
    margin-top: 2px;
    font-size: ${theme.font.small};
    color: ${theme.color.textMuted};
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

export const SectionHeader = ({ title, subtitle, children, inset, divided }) => (
  <PanelHead $inset={inset} $divided={divided}>
    <div>
      <h3>{title}</h3>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {children && <div className="actions">{children}</div>}
  </PanelHead>
);

/* --------------------------------- Buttons -------------------------------- */

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: ${theme.radius.sm};
  font-size: ${theme.font.body};
  font-weight: 500;
  font-family: inherit;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const Button = styled.button`
  ${buttonBase};
  border: 1px solid
    ${({ $variant }) =>
      $variant === "primary" ? theme.color.brandDark : theme.color.borderStrong};
  background: ${({ $variant }) =>
    $variant === "primary" ? theme.color.brandDark : theme.color.surface};
  color: ${({ $variant }) =>
    $variant === "primary" ? "#fff" : theme.color.text};

  &:hover:not(:disabled) {
    background: ${({ $variant }) =>
      $variant === "primary" ? theme.color.brand : theme.color.surfaceAlt};
  }
`;

// Same visual language for react-router <Link>s.
export const linkButtonStyles = css`
  ${buttonBase};
  border: 1px solid ${theme.color.borderStrong};
  background: ${theme.color.surface};
  color: ${theme.color.text};

  &:hover {
    background: ${theme.color.surfaceAlt};
  }

  &.primary {
    border-color: ${theme.color.brandDark};
    background: ${theme.color.brandDark};
    color: #fff;

    &:hover {
      background: ${theme.color.brand};
    }
  }
`;

/* -------------------------------- Stat card ------------------------------- */

const StatCardBox = styled.div`
  position: relative;
  background: ${theme.color.surface};
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.sm};
  padding: 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
    background: ${({ $accent }) => $accent};
  }

  &:hover {
    box-shadow: ${theme.shadow.md};
    transform: translateY(-1px);
  }

  .label {
    font-size: ${theme.font.small};
    color: ${theme.color.textMuted};
    font-weight: 500;
  }

  .value {
    margin-top: 10px;
    font-size: 32px;
    line-height: 1.1;
    font-weight: 700;
    color: ${theme.color.text};
    font-variant-numeric: tabular-nums;
  }

  .hint {
    margin-top: 8px;
    font-size: ${theme.font.tiny};
    color: ${theme.color.textFaint};
  }

  .glyph {
    flex: none;
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    border-radius: ${theme.radius.md};
    background: ${({ $accent }) => `${$accent}14`};
    color: ${({ $accent }) => $accent};
    font-size: 18px;
  }
`;

export const StatCard = ({ label, value, hint, glyph, accent }) => (
  <StatCardBox $accent={accent || theme.color.brand}>
    <div>
      <div className="label">{label}</div>
      <div className="value">{value ?? "—"}</div>
      {hint && <div className="hint">{hint}</div>}
    </div>
    {glyph && <div className="glyph">{glyph}</div>}
  </StatCardBox>
);

export const StatGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

/* ---------------------------------- Tabs ---------------------------------- */

const TabsBar = styled.div`
  display: inline-flex;
  padding: 4px;
  gap: 4px;
  background: ${theme.color.surfaceAlt};
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.pill};

  button {
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: ${theme.font.small};
    font-weight: 500;
    color: ${theme.color.textMuted};
    padding: 8px 16px;
    border-radius: ${theme.radius.pill};
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      color: ${theme.color.text};
    }

    &[aria-selected="true"] {
      background: ${theme.color.brandDark};
      color: ${theme.color.accent};
    }
  }
`;

export const SegmentedTabs = ({ options, value, onChange }) => (
  <TabsBar role="tablist">
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        role="tab"
        aria-selected={value === option.value}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </TabsBar>
);

/* --------------------------------- Select --------------------------------- */

export const Select = styled.select`
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${theme.color.borderStrong};
  border-radius: ${theme.radius.sm};
  background: ${theme.color.surface};
  font-family: inherit;
  font-size: ${theme.font.body};
  color: ${theme.color.text};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.color.brand};
    box-shadow: 0 0 0 3px ${theme.color.brandSoft};
  }
`;

/* -------------------------------- Page head ------------------------------- */

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;

  h1 {
    font-size: ${theme.font.h1};
    font-weight: 700;
    color: ${theme.color.text};
    line-height: 1.25;
  }

  p {
    margin-top: 6px;
    font-size: ${theme.font.body};
    color: ${theme.color.textMuted};
  }

  .actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
`;

export const PageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
